import React, { useCallback, useEffect, useRef, useState } from 'react';

export function getPercentFromMouseEvent(
  e: MouseEvent | React.MouseEvent,
  el: HTMLElement
): number {
  const rect = el.getBoundingClientRect();
  const x = e.clientX - rect.left;
  return Math.max(0, Math.min(1, x / rect.width));
}

export type UseScrubberParams = {
  isLoading: boolean;
  onSeek: (percent: number) => void;
  onScrubStart?: () => void;
};

export type ScrubberState = {
  trackRef: React.RefObject<HTMLDivElement>;
  isDragging: boolean;
  hoverPercent: number | null;
  handleTrackMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleTrackMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleTrackMouseLeave: () => void;
};

/**
 * Encapsulates the click-and-drag scrubbing behaviour for a horizontal track:
 * local drag/hover state, the track event handlers, and the window-level
 * listeners that keep dragging alive when the cursor leaves the track.
 */
export function useScrubber({
  isLoading,
  onSeek,
  onScrubStart,
}: UseScrubberParams): ScrubberState {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPercent, setHoverPercent] = useState<number | null>(null);

  const handleTrackMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isLoading || !trackRef.current) return;
      onScrubStart?.();
      setIsDragging(true);
      onSeek(getPercentFromMouseEvent(e, trackRef.current));
    },
    [isLoading, onSeek, onScrubStart]
  );

  const handleTrackMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    setHoverPercent(getPercentFromMouseEvent(e, trackRef.current));
  }, []);

  const handleTrackMouseLeave = useCallback(() => {
    setHoverPercent(null);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!trackRef.current) return;
      onSeek(getPercentFromMouseEvent(e, trackRef.current));
    };

    const onMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, onSeek]);

  return {
    trackRef,
    isDragging,
    hoverPercent,
    handleTrackMouseDown,
    handleTrackMouseMove,
    handleTrackMouseLeave,
  };
}
